type SectionStatus = 'complete' | 'in-progress' | 'not-started';

interface SectionMeta {
  id: string;
  title: string;
  index: number;
  description: string;
  status: SectionStatus;
}

interface Suggestion {
  id: string;
  label: string;
  prompt: string;
  sectionId: string;
}

interface SubgroupRates {
  white: number;
  hispanic_latino: number;
  black: number;
  iep: number;
  multilingual_learners: number;
}

interface Prototype3Facts {
  district: { name: string; cohort: string; year: string };
  cohort_size: number;
  postsecondary_readiness: {
    overall_pct: number;
    by_route: { college_only: number; career_only: number; both: number };
    lagging_subgroups: { iep: number; black: number; hispanic_latino: number; multilingual_learners: number };
  };
  college_readiness: {
    overall_pct: number;
    measures: { coursework_intensity: string; gpa_threshold: string; sat_threshold: string };
    by_subgroup: SubgroupRates;
  };
  cte: {
    career_prepared_only_pct: number;
  };
  source_notes: { slides_28_29: string; slides_30_51: string; gibson_ek: string };
}

const SECTIONS: SectionMeta[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    index: 1,
    description: 'EOAA audit overview and Issaquah district background.',
    status: 'complete',
  },
  {
    id: 'postsecondary-readiness',
    title: 'Postsecondary Readiness',
    index: 2,
    description: 'Readiness rates, route breakdown, and equity gaps for the Class of 2024.',
    status: 'in-progress',
  },
  {
    id: 'college-eligibility',
    title: 'College Eligibility',
    index: 3,
    description: 'Eligibility requirements and rates across student subgroups.',
    status: 'not-started',
  },
  {
    id: 'cte',
    title: 'Career & Technical Education',
    index: 4,
    description: 'CTE enrollment, completion, and pathway outcomes.',
    status: 'not-started',
  },
  {
    id: 'graduation-rates',
    title: 'Graduation Rates',
    index: 5,
    description: 'District graduation rates and subgroup disparities.',
    status: 'not-started',
  },
  {
    id: 'attendance',
    title: 'Attendance & Chronic Absenteeism',
    index: 6,
    description: 'Attendance trends and chronic absenteeism by school and subgroup.',
    status: 'not-started',
  },
  {
    id: 'school-climate',
    title: 'School Climate & Safety',
    index: 7,
    description: 'Stakeholder survey results and safety indicators.',
    status: 'not-started',
  },
];

const FACTS: Prototype3Facts = {
  district: { name: 'Issaquah School District', cohort: 'Class of 2024', year: '2023-24' },
  cohort_size: 2310,
  postsecondary_readiness: {
    overall_pct: 76,
    by_route: { college_only: 16, career_only: 25, both: 35 },
    lagging_subgroups: { iep: 29, black: 45, hispanic_latino: 51, multilingual_learners: 56 },
  },
  college_readiness: {
    overall_pct: 51,
    measures: {
      coursework_intensity: 'Completing academically intense coursework',
      gpa_threshold: 'Earning a grade point average (GPA) of 3.33 or higher',
      sat_threshold: 'Scoring 1120 or higher on the SAT (or an equivalent score on the ACT)',
    },
    by_subgroup: {
      white: 58,
      hispanic_latino: 35,
      black: 32,
      iep: 18,
      multilingual_learners: 24,
    },
  },
  cte: { career_prepared_only_pct: 25 },
  source_notes: {
    slides_28_29: "Slides 28-29 show subgroup gap visualizations; combine slider graphs per Keith's note.",
    slides_30_51:
      'Slides 30 and 51 combine into a wayfinder visualization (76% purple, blending college red + CTE blue).',
    gibson_ek: 'Gibson EK School is non-traditional, project-based, with no traditional grading.',
  },
};

const INTRODUCTION_DRAFT = `### Introduction

This Educational Outcomes Audit & Analysis (EOAA) examines the Issaquah School District's Class of 2024 cohort. The audit synthesizes graduation, postsecondary readiness, and equity indicators to surface where the district is making progress and where structural gaps remain.`;

const INITIAL_DRAFTS: Record<string, string> = {
  introduction: INTRODUCTION_DRAFT,
};

const INITIAL_SECTION_STATUSES: Record<string, SectionStatus> = Object.fromEntries(
  SECTIONS.map((s) => [s.id, s.status])
);

const SUGGESTIONS: Suggestion[] = [
  {
    id: 'start-introduction',
    label: 'Start with the Introduction',
    prompt: "Let's begin with the Introduction. Set up the EOAA framing for Issaquah's Class of 2024.",
    sectionId: 'introduction',
  },
  {
    id: 'review-postsecondary',
    label: 'Review postsecondary readiness data',
    prompt:
      "Walk me through the postsecondary readiness data. The big story is that 76% of Issaquah's Class of 2024 were postsecondary ready — break down college-ready only, career-prepared only, and both, and call out the four lagging subgroups.",
    sectionId: 'postsecondary-readiness',
  },
];

const isQuestion = (text: string): boolean => {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return false;
  if (trimmed.endsWith('?')) return true;
  return /^(what|how|why|when|where|who|which|can you|could you|do you|is there|are there|tell me|show me|explain)\b/.test(
    trimmed
  );
};

const isEditCommand = (text: string): boolean => {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return false;
  return /\b(update|rewrite|edit|revise|redo|polish|improve|fix|shorten|lengthen|tighten|expand|condense|clarify|simplify|reword|rephrase|reformat|replace|draft|tweak|adjust|refine|cut|trim|combine|split|reorder|restructure|make (?:it|this|the|them|that)|add (?:a |an |more |another )?(?:paragraph|sentence|section|line|note|detail|context)|remove (?:a |the |this )?(?:paragraph|sentence|section|line))\b/.test(
    trimmed
  );
};

const DRAFT_BEGIN_MARKER = '<<<DRAFT_BEGIN>>>';
const DRAFT_END_MARKER = '<<<DRAFT_END>>>';

const DRAFT_BEGIN_RE = /<<<DRAFT_BEGIN(?::([a-z0-9-]+))?>>>/;
const DRAFT_END_RE = /<<<DRAFT_END>>>/;
const APPROVE_RE = /<<<APPROVE(?::([a-z0-9-]+))?>>>/;
const APPROVE_GLOBAL_RE = /<<<APPROVE(?::[a-z0-9-]+)?>>>/g;
const SWITCH_RE = /<<<SWITCH:([a-z0-9-]+)>>>/;
const SWITCH_GLOBAL_RE = /<<<SWITCH:[a-z0-9-]+>>>/g;

interface ParsedDraft {
  sectionId?: string;
  body: string;
}

interface ParsedApprove {
  sectionId?: string;
}

interface ParsedSwitch {
  sectionId: string;
}

interface ParsedChatResponse {
  chat: string;
  draft?: ParsedDraft;
  approve?: ParsedApprove;
  switchTo?: ParsedSwitch;
}

const parseChatResponse = (raw: string): ParsedChatResponse => {
  let text = raw ?? '';
  let draft: ParsedDraft | undefined;

  const beginMatch = text.match(DRAFT_BEGIN_RE);
  if (beginMatch && beginMatch.index !== undefined) {
    const beginIdx = beginMatch.index;
    const afterBegin = beginIdx + beginMatch[0].length;
    const tail = text.slice(afterBegin);
    const endMatch = tail.match(DRAFT_END_RE);
    const endRel = endMatch?.index;
    const body = endRel === undefined ? tail : tail.slice(0, endRel);
    const trimmedBody = body.trim();
    if (trimmedBody) {
      draft = { sectionId: beginMatch[1] || undefined, body: trimmedBody };
    }
    const endAbs = endRel === undefined ? text.length : afterBegin + endRel + (endMatch?.[0].length ?? 0);
    text = `${text.slice(0, beginIdx)}\n${text.slice(endAbs)}`;
  }

  const approveMatch = text.match(APPROVE_RE);
  const approve: ParsedApprove | undefined = approveMatch ? { sectionId: approveMatch[1] || undefined } : undefined;
  if (approve) text = text.replace(APPROVE_GLOBAL_RE, '');

  const switchMatch = text.match(SWITCH_RE);
  const switchTo: ParsedSwitch | undefined = switchMatch ? { sectionId: switchMatch[1] } : undefined;
  if (switchTo) text = text.replace(SWITCH_GLOBAL_RE, '');

  const chat = text
    .replace(/<<<[A-Z_]+(?::[a-z0-9-]+)?>>>/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { chat, draft, approve, switchTo };
};

export {
  SECTIONS,
  FACTS,
  SUGGESTIONS,
  INITIAL_DRAFTS,
  INITIAL_SECTION_STATUSES,
  isQuestion,
  isEditCommand,
  parseChatResponse,
  DRAFT_BEGIN_MARKER,
  DRAFT_END_MARKER,
};
export type { SectionMeta, SectionStatus, Suggestion };
